/**
 * Date-filter helpers for episode queries against `episodes.created_at`
 * (a `timestamptz`).
 *
 * The webapp surfaces episodes with bare `YYYY-MM-DD` date pickers and
 * routes that emit bare date strings, but the team thinks in Pacific
 * Time. The collector-insights backend already groups by
 * America/Los_Angeles calendar day, while the Episodes page historically
 * interpreted bare dates as UTC days -- which silently dropped 7-8 hours
 * of episodes per day at the boundary, and made click-through counts
 * disagree with the chart that produced them.
 *
 * Centralizing the bound normalization here means "4/13" means the same
 * thing on both pages: PT 4/13 00:00 -> PT 4/13 23:59:59.999, expressed
 * as the matching UTC instants.
 *
 * Full ISO timestamps (length != 10) are passed through unchanged so
 * callers that have already computed timezone-aware bounds can opt out.
 */

const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isBareDate(s: string): boolean {
  return BARE_DATE_RE.test(s);
}

/**
 * Returns the UTC offset (in minutes) of America/Los_Angeles at the given
 * UTC instant. Negative for PT (e.g. -420 for PDT, -480 for PST).
 *
 * `hourCycle: "h23"` is pinned explicitly so midnight is always reported
 * as hour 0 with the current day. Under the alternative h24 cycle,
 * midnight is reported as hour 24 with the *previous* day, which would
 * require a day++ adjustment. Pinning h23 keeps the math trivial across
 * runtimes regardless of how the en-US locale defaults resolve.
 */
export function ptOffsetMinutesAtInstant(utcInstant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(utcInstant);

  const map: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = parseInt(p.value, 10);
  }
  const ptAsUtc = Date.UTC(
    map.year,
    map.month - 1,
    map.day,
    map.hour,
    map.minute,
    map.second,
  );
  return (ptAsUtc - utcInstant.getTime()) / 60_000;
}

/**
 * Returns the UTC instant corresponding to midnight (start of day) in
 * America/Los_Angeles for a given YYYY-MM-DD date string.
 *
 * The offset is probed at 01:00 UTC on the target date. Both DST
 * transitions happen at 02:00 PT (~09:00-10:00 UTC), so 01:00 UTC always
 * sits in the offset window that PT midnight itself uses.
 */
export function ptMidnightUtc(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d, 1, 0, 0));
  const offsetMin = ptOffsetMinutesAtInstant(probe);
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0) - offsetMin * 60_000);
}

/**
 * Normalize a `date_from` (lower-bound, used with `gte`) value.
 *
 * Bare YYYY-MM-DD -> UTC ISO at PT midnight.
 * Anything else -> passed through unchanged (callers may pre-compute
 * a timezone-specific bound).
 */
export function normalizeEpisodeDateLowerBound(dateFrom: string): string {
  if (!isBareDate(dateFrom)) return dateFrom;
  return ptMidnightUtc(dateFrom).toISOString();
}

/**
 * Normalize a `date_to` (upper-bound, used with `lte`) value.
 *
 * Bare YYYY-MM-DD -> UTC ISO at PT end-of-day, computed as
 * (PT midnight of the next day) - 1 millisecond. Doing it this way
 * avoids a separate DST probe at 23:59 PT.
 *
 * Anything else -> passed through unchanged.
 */
export function normalizeEpisodeDateUpperBound(dateTo: string): string {
  if (!isBareDate(dateTo)) return dateTo;
  const [y, m, d] = dateTo.split('-').map(Number);
  // Construct next-day string via Date.UTC so month/year rollovers are correct.
  const nextDayStr = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  return new Date(ptMidnightUtc(nextDayStr).getTime() - 1).toISOString();
}
